import type { CurrentUser } from '@xfile/shared';
import { useAuth } from './AuthProvider';

/**
 * L'utilisateur ADMINISTRE-T-IL le dépôt ? Source UNIQUE de vérité côté SPA pour les affordances
 * d'ADMINISTRATION ouvertes aux deux rôles élevés (corbeille `/trash`, XFILE-72), alignée mot pour
 * mot sur la règle du serveur :
 *
 *   administrateur = rôle `admin` OU rôle `superadmin`.
 *
 * À distinguer de `useIsSuperadmin` (rôle STRICT : la console `/admin` n'est ouverte qu'au
 * superadmin) — les deux gardes coexistent et ne recouvrent pas le même périmètre.
 *
 * ⚠ NE PAS RÉINTRODUIRE DE « DROIT D'ÉCRITURE GLOBAL » pour s'en servir ici. Un helper de ce nom
 * (`useCanWrite`, avec sa garde de route `WriteRoute`) a existé et portait la MÊME formule ; il a
 * été supprimé en v2.8.0, à la fois parce qu'il était sans appelant et parce que la question à
 * laquelle il répondait — « ce compte a-t-il un droit d'écriture global ? » — n'a plus de sens
 * depuis que l'écriture se décide par ESPACE (`folderCapabilities`, XFILE-25) : un rôle `user`
 * dépose, crée et supprime dans l'espace Public et dans le sien. Un rôle sert à ouvrir une
 * AFFORDANCE D'ADMINISTRATION, jamais à décider d'un droit sur un dossier ou un fichier — c'est la
 * confusion des deux qui a produit XFILE-73.
 *
 * L'UI MASQUE (pas seulement désactive) les affordances d'administration pour tout autre rôle ;
 * `GET /api/deletions` répond 403 et `AdminRoute` ferme aussi l'accès par URL. Centralise le
 * calcul — importer ce helper, ne pas le redupliquer.
 */
export function isAdminFor(user: CurrentUser | null): boolean {
  if (user === null) return false;
  return user.role === 'admin' || user.role === 'superadmin';
}

/** Variante hook : lit l'utilisateur actif du contexte d'auth. */
export function useIsAdmin(): boolean {
  const { user } = useAuth();
  return isAdminFor(user);
}
